import { useEffect } from 'react';
import { useAppSettingsShallowStore, useAppSettingsStore } from '@frontend/app-settings-store';
import { SoftphoneStateInterface } from '@frontend/softphone-state';
import { TAB_ID, registerOpenTab } from '@frontend/tab-management';

type ActiveTabEventType = {
  type: 'SOFTPHONE_TAB';
  action: 'REGISTER' | 'UNREGISTER' | 'CLEAR' | 'ACTIVATE' | 'SYNC';
  payload: {
    activeId: string;
    tabIds: string[];
    hasActiveCall: boolean;
    hasSoftphoneRegistered: boolean;
    lastRegisteredCallId: string;
  };
};

type SoftphoneStateEventType = {
  type: 'SOFTPHONE';
  payload:
    | { action: 'CONNECTED' | 'DISCONNECTED'; tabId: string }
    | { action: 'ACTIVE_CALL'; tabId: string; hasActiveCall: boolean }
    | { action: 'LAST_REGISTERED_CALL_ID'; tabId: string; callId: string }
    | { action: 'REGISTERED_SOFTPHONE'; tabId: string; hasSoftphoneRegistered: boolean };
};

export const useTabManagement = () => {
  const { registerTab, unregisterTab } = SoftphoneStateInterface;
  const { setActiveTab, setOverrideSoftphoneCheck, setHasActiveSoftphoneCalls } = useAppSettingsShallowStore(
    'setActiveTab',
    'setOverrideSoftphoneCheck',
    'setHasActiveSoftphoneCalls'
  );

  useEffect(() => {
    const beforeUnloadFn = (e: BeforeUnloadEvent) => {
      // Access current values at event time
      const { hasActiveSoftphoneCalls, activeTab } = useAppSettingsStore.getState();

      if (hasActiveSoftphoneCalls && activeTab === TAB_ID) {
        e.preventDefault();
        e.returnValue = '';
      } else {
        unregisterTab(TAB_ID);
      }
    };

    window.addEventListener('beforeunload', beforeUnloadFn);
    registerTab(TAB_ID);
    const unregisterOpenTab = registerOpenTab(TAB_ID);

    return () => {
      unregisterTab(TAB_ID);
      unregisterOpenTab();
      window.removeEventListener('beforeunload', beforeUnloadFn);
    };
  }, [registerTab, unregisterTab]);

  useEffect(() => {
    const update = (event: MessageEvent<ActiveTabEventType | SoftphoneStateEventType>) => {
      if (event.data.type === 'SOFTPHONE_TAB' && event.data.action === 'SYNC') {
        const { activeTab } = useAppSettingsStore.getState();
        setActiveTab(event.data.payload.activeId);

        /**
         * If this instance is no longer the active tab, we need to remove the override.
         *
         * NOTE: This works only within the same client/browser.
         */
        if (event.data.payload.activeId !== TAB_ID) {
          if (activeTab === TAB_ID) {
            window.dispatchEvent(new Event('softphone:deactivate'));
          }
          setOverrideSoftphoneCheck(false);
        }
      }

      if (event.data.type === 'SOFTPHONE' && event.data.payload.tabId !== TAB_ID) {
        if (event.data.payload.action === 'ACTIVE_CALL') {
          setHasActiveSoftphoneCalls(event.data.payload.hasActiveCall);
        }
      }
    };

    if ('serviceWorker' in navigator && navigator.serviceWorker) {
      navigator.serviceWorker.addEventListener('message', update);
    }

    return () => {
      if ('serviceWorker' in navigator && navigator.serviceWorker) {
        navigator.serviceWorker.removeEventListener('message', update);
      }
    };
  }, [setActiveTab, setOverrideSoftphoneCheck]);
};
